import { useCallback, useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import hustleMadLogo from '../../images/Footer/logo-dark-blue.svg'
import styles from './Auth.module.css'
import axios from 'axios'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useAuth } from '../../providers/AuthProvider'

export const Auth = () => {
  const navigate = useNavigate()
  const redirectUrl = useLocation().state?.redirectUrl || '/'
  const [type, setType] = useState('login')
  const [data, setData] = useState({
    email: '',
    password: '',
    phone: '',
    name: '',
    companyName: '',
  })
  const [emailError, setEmailError] = useState('')
  const { user, loading , fetchUser} = useAuth()

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      navigate('/')
    }
  }, [user, navigate, redirectUrl])

  const login = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/user/login`,
        data
      )
      localStorage.setItem('token', response.data.token)
      return true
    } catch (error) {
      console.log(error)
      toast.error(error.response.data.message ?? 'Login failed')
      return false
    }
  }

  const signup = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/user/signup`,
        data
      )
      localStorage.setItem('token', response.data.token)
      toast.success('Signup successful')
      return true
    } catch (error) {
      console.log(error)
      toast.error(error.response.data.message ?? 'Signup failed')
      return false
    }
  }

  const handleSubmit = useCallback(async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (data.email && !emailRegex.test(data.email)) {
      setEmailError('Please enter a valid email address')
      return
    } else {
      setEmailError('')
    }
    
    let success = false
    if (type === 'login') {
      success = await login()
    } else {
      success = await signup()
    }
    
    if (success) {
      await fetchUser()
      navigate(redirectUrl)
    }
  }, [type, data, fetchUser, navigate, redirectUrl])

  const inputOptions = useMemo(() => {
    let options = [
      {
        title: 'Email',
        name: 'email',
        type: 'email',
        placeholder: 'xyz@gmail.com',
      },
      {
        title: 'Password',
        name: 'password',
        type: 'password',
        placeholder: 'Enter your Password',
      },
    ]
    if (type === 'signup') {
      options.push(
        {
          title: 'Name',
          name: 'name',
          type: 'text',
          placeholder: 'Enter your Name',
        },
        {
          title: 'Phone',
          name: 'phone',
          type: 'tel',
          placeholder: 'Enter your Phone',
        },
        {
          title: 'Company Name',
          name: 'companyName',
          type: 'text',
          placeholder: 'Enter your Company Name',
        }
      )
    }
    return options
  }, [type])

  const handleChange = (e) => {
    const { name, value } = e.target
    if (e.target.name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (value && emailRegex.test(value)) {
        setEmailError('')
      }
    }
    setData({ ...data, [name]: value })
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <img src={hustleMadLogo} alt='HustleMad Logo' />
        <h1>{type === 'login' ? 'Welcome Back' : 'Create an Account'}</h1>
        <h6>
          {type === 'login'
            ? 'Enter your email and password to access your account'
            : 'Enter your name, email and password to create an account'}
        </h6>

        <div className={styles.form}>
          {inputOptions.map((option) => (
            <div key={option.name} className={styles.inputGroup}>
              <label className={styles.label}>{option.title}</label>
              <FormInput
                option={option}
                value={data[option.name]}
                onChange={handleChange}
                error={option.name === 'email' ? emailError : ''}
              />
              {option.name === 'email' && emailError && (
                <span className={styles.errorText} style={{ color: 'red' }}>
                  {emailError}
                </span>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={(e) => {
            e.preventDefault()
            handleSubmit()
          }}
          className={styles.button}
        >
          {type === 'login' ? 'Log In' : 'Sign Up'}
        </button>
        <h6>
          {type === 'login'
            ? "Don't have an account?"
            : 'Already have an account?'}
          <span onClick={() => setType(type === 'login' ? 'signup' : 'login')}>
            {type === 'login' ? 'Sign up Now' : 'Log in'}
          </span>
        </h6>
      </div>
    </div>
  )
}

const PasswordInput = ({ name, value, onChange, placeholder }) => {
  const [showPassword, setShowPassword] = useState(false)

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <div className={styles.inputWrapper}>
      <input
        type={showPassword ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={styles.input}
      />
      <button
        type='button'
        className={styles.passwordToggle}
        onClick={togglePasswordVisibility}
        aria-label={showPassword ? 'Hide password' : 'Show password'}
      >
        <FontAwesomeIcon
          icon={showPassword ? faEye : faEyeSlash}
          className={styles.eyeIcon}
        />
      </button>
    </div>
  )
}

const FormInput = ({ option, value, onChange, error }) => {
  if (option.type === 'password') {
    return (
      <PasswordInput
        name={option.name}
        value={value}
        onChange={onChange}
        placeholder={option.placeholder}
      />
    )
  }

  return (
    <div className={styles.inputWrapper}>
      <input
        type={option.type}
        name={option.name}
        value={value}
        onChange={onChange}
        placeholder={option.placeholder}
        className={`${styles.input} ${
          option.name === 'email' && error ? styles.inputError : ''
        }`}
      />
    </div>
  )
}
